import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import { List, ListItem, ListItemButton } from "@mui/material";
import useAppMenuRoutes from "../useAppMenuRoutes";
import SvgIcon from "../../helpers/SvgIcon";

const DrawerItems = ({
  handleDrawerClose,
}: {
  handleDrawerClose?: () => void;
}) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const categories = useAppMenuRoutes();
  const [expanded, setExpanded] = useState<string | false>(false);

  const activeRoute = categories
    .flatMap(({ title, categories: links }) =>
      links.map(({ path }) => ({ section: title, path })),
    )
    .filter(({ path }) => pathname === path || pathname.startsWith(`${path}/`))
    .sort((a, b) => b.path.length - a.path.length)[0];

  useEffect(() => {
    if (activeRoute?.section) {
      setExpanded(activeRoute.section);
    }
  }, [activeRoute?.section]);

  const handleNavigate = (path: string) => {
    navigate(path);
    handleDrawerClose?.();
  };

  return (
    <nav
      aria-label="Meniu principal"
      className="min-h-0 flex-1 overflow-y-auto px-3 py-4">
      {categories.map(({ title, icon, categories: links }, index) => {
        const isExpanded = expanded === title;
        const isCurrentSection = activeRoute?.section === title;

        return (
          <Accordion
            key={title}
            disableGutters
            elevation={0}
            expanded={isExpanded}
            onChange={(_, shouldExpand) =>
              setExpanded(shouldExpand ? title : false)
            }
            className="!my-0 !bg-transparent !shadow-none before:!hidden">
            <AccordionSummary
              id={`drawer-section-${index}`}
              aria-controls={`drawer-section-${index}-content`}
              expandIcon={
                <SvgIcon
                  type="EXPAND_MORE"
                  fill="currentColor"
                  className="!h-5 !w-5"
                />
              }
              className={`!min-h-[52px] !rounded-xl !px-3 transition-colors [&_.MuiAccordionSummary-content]:!my-0 [&_.MuiAccordionSummary-content]:!items-center [&_.MuiAccordionSummary-expandIconWrapper]:!text-muted-dark ${
                isCurrentSection
                  ? "!bg-accent-subtle !text-accent"
                  : isExpanded
                  ? "!bg-surface-subtle !text-slate-900"
                  : "!text-slate-700 hover:!bg-surface-hover"
              }`}>
              <span
                className={`mr-3 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
                  isCurrentSection
                    ? "bg-white text-accent"
                    : "bg-surface-subtle text-muted-dark"
                }`}>
                <SvgIcon
                  type={icon}
                  fill="currentColor"
                  className="!h-5 !w-5"
                />
              </span>
              <span className="text-[15.5px] font-semibold">{title}</span>
            </AccordionSummary>
            <AccordionDetails className="!px-2 !pb-2 !pt-1">
              <List disablePadding>
                {links.map(({ title: linkTitle, path }) => {
                  const isActive = activeRoute?.path === path;

                  return (
                    <ListItem
                      key={path}
                      disablePadding
                      className="!mb-1 last:!mb-0">
                      <ListItemButton
                        aria-current={isActive ? "page" : undefined}
                        onClick={() => handleNavigate(path)}
                        className={`!min-h-[40px] !rounded-lg !py-2 !pl-12 !pr-3 ${
                          isActive
                            ? "!bg-accent-subtle !text-accent"
                            : "!text-slate-600 hover:!bg-surface-hover"
                        }`}>
                        <span
                          className={`mr-3 h-1.5 w-1.5 shrink-0 rounded-full ${
                            isActive ? "bg-accent" : "bg-slate-300"
                          }`}
                        />
                        <span className="text-[14.5px] font-medium leading-5">
                          {linkTitle}
                        </span>
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </nav>
  );
};

export default DrawerItems;
